import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';

import {
  Box,
  Button,
  Divider,
  Paper,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';

import { DataGrid, GridToolbar, GridActionsCellItem } from '@mui/x-data-grid';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import DeleteIcon from '@mui/icons-material/Delete';

import { apiUrl } from '../../apiUrl/ApiUrl';

export default function ProductionInput() {
  const API_CREATE = `${apiUrl}/MatCard/CreateNewDay`;
  const API_SEARCH = `${apiUrl}/MatCard/SearchDailyproductioncapacity`;
  const API_UPDATE = `${apiUrl}/MatCard/UpdateDailyproductioncapacity`;
  const API_DELETE = `${apiUrl}/MatCard/DeleteDailyproductioncapacity`;

  // 新增表單
  const [productionDate, setProductionDate] = useState(dayjs());
  const [machine, setMachine] = useState('');
  const [operator, setOperator] = useState('');
  const [drawingNo, setDrawingNo] = useState('');
  const [qty, setQty] = useState('');
  const [attr, setAttr] = useState('');
  const [dept, setDept] = useState('');

  // 查詢條件
  const [qStart, setQStart] = useState(dayjs().startOf('month'));
  const [qEnd, setQEnd] = useState(dayjs());
  const [qDrawingNo, setQDrawingNo] = useState('');
  const [qOperator, setQOperator] = useState('');
  const [qAttr, setQAttr] = useState('全部');
  const [qDept, setQDept] = useState('全部');

  // 列表
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // 刪除確認
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // 訊息
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const showToast = (severity, message) => setToast({ open: true, severity, message });

  const columns = useMemo(() => ([
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'batchNo', headerName: '批號', width: 140, editable: false },
    {
      field: 'productionDate',
      headerName: '生產日期',
      width: 120,
      valueFormatter: (params) => (params.value ? dayjs(params.value).format('YYYY/MM/DD') : ''),
      editable: true,
    },
    { field: 'dept', headerName: '部門', width: 90, editable: true },
    { field: 'property', headerName: '屬性', width: 95, editable: true },
    { field: 'machine', headerName: '機台', width: 110, editable: true },
    { field: 'operator', headerName: '作業人員', width: 120, editable: true },
    { field: 'drawingNo', headerName: '圖號', width: 150, editable: true },
    { field: 'qty', headerName: '數量', width: 90, type: 'number', editable: true },

    {
      field: 'posted',
      headerName: '入帳',
      width: 90,
      type: 'boolean',
      editable: true,
      valueGetter: (params) => {
        const v = params.row?.posted;
        if (v === true || v === 1 || v === '1' || v === 'Y') return true;
        return false;
      },
    },

    { field: 'record', headerName: '紀錄', width: 110, editable: false },
    {
      field: 'inputTime',
      headerName: '輸入時間',
      width: 170,
      editable: false,
      valueFormatter: (params) => (params.value ? dayjs(params.value).format('YYYY/MM/DD HH:mm:ss') : ''),
    },

    {
      field: 'actions',
      type: 'actions',
      headerName: '操作',
      width: 90,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="刪除"
          onClick={() => {
            setDeleteTarget(params.row);
            setDeleteOpen(true);
          }}
          showInMenu={false}
        />
      ],
    }
  ]), []);

  // 新增
  const handleCreate = async () => {
    if (!productionDate || !machine || !operator || !drawingNo || qty === '') {
      showToast('warning', '請把欄位填完整（生產日期、機台、作業人員、圖號、數量）');
      return;
    }
    if (Number.isNaN(Number(qty)) || Number(qty) <= 0) {
      showToast('warning', '數量需為大於 0 的數字');
      return;
    }

    const payload = {
      productionDate: productionDate.format('YYYY/MM/DD'),
      machine: machine.trim(),
      operatorName: operator.trim(),
      drawingNo: drawingNo.trim(),
      qty: Number(qty),
      property: attr || '',
      dept: dept || '',
    };

    try {
      setLoading(true);
      await axios.post(API_CREATE, payload);
      showToast('success', '新增完成');
      await handleSearch();
      setQty('');
    } catch (err) {
      console.error(err);
      showToast('error', '新增失敗，請查看 API 或 console');
    } finally {
      setLoading(false);
    }
  };

  const handleResetCreate = () => {
    setProductionDate(dayjs());
    setMachine('');
    setOperator('');
    setDrawingNo('');
    setQty('');
    setAttr('');
    setDept('');
  };

  // 查詢
  const handleSearch = async () => {
    const start = qStart?.format('YYYY/MM/DD');
    const end = qEnd?.format('YYYY/MM/DD');

    try {
      setLoading(true);
      const res = await axios.get(API_SEARCH, {
        params: {
          start,
          end,
          drawingNo: qDrawingNo?.trim() || '',
          operator: qOperator?.trim() || '',
          attr: qAttr === '全部' ? '' : qAttr,
          dept: qDept === '全部' ? '' : qDept,
        },
      });

      const list = Array.isArray(res.data) ? res.data : [];
      setRows(list);
      showToast('success', `查詢完成：${list.length} 筆`);
    } catch (err) {
      console.error(err);
      showToast('error', '查詢失敗，請查看 API 或 console');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSearch = () => {
    setQStart(dayjs().startOf('month'));
    setQEnd(dayjs());
    setQDrawingNo('');
    setQOperator('');
    setQAttr('全部');
    setQDept('全部');
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 修改
  const processRowUpdate = async (newRow, oldRow) => {
    const payload = {
      id: newRow.id,
      productionDate: dayjs(newRow.productionDate).isValid()
        ? dayjs(newRow.productionDate).format('YYYY/MM/DD')
        : null,
      machine: (newRow.machine || '').trim(),
      operatorName: (newRow.operator || '').trim(),
      drawingNo: (newRow.drawingNo || '').trim(),
      qty: Number(newRow.qty),
      property: (newRow.property || '').trim(),
      dept: (newRow.dept || '').trim(),
      posted: newRow.posted === true || newRow.posted === 1 || newRow.posted === 'Y',
    };

    if (!payload.productionDate || !payload.machine || !payload.operatorName || !payload.drawingNo || !(payload.qty > 0)) {
      showToast('warning', '修改資料不完整或數量不正確，已取消更新');
      return oldRow;
    }

    try {
      setLoading(true);
      await axios.put(API_UPDATE, payload);
      showToast('success', '更新完成');
      return { ...newRow, ...payload, operator: payload.operatorName };
    } catch (err) {
      console.error(err);
      showToast('error', '更新失敗，已回復原資料');
      return oldRow;
    } finally {
      setLoading(false);
    }
  };

  // 刪除
  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;

    try {
      setLoading(true);
      await axios.delete(API_DELETE, { params: { id: deleteTarget.id } });
      showToast('success', '刪除完成');
      setDeleteOpen(false);
      setDeleteTarget(null);
      await handleSearch();
    } catch (err) {
      console.error(err);
      showToast('error', '刪除失敗，請查看 API 或 console');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          width: '100%',
          overflowX: 'hidden',     // ✅ 防止水平溢出
          mx: 0,
          px: 0,
          pl: '0.5vw',
          pr: '0.5vw',
          py: 2,
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
            產量輸入
          </Typography>

          <Stack direction="row" spacing={1}>
            <Chip label={`本月：${dayjs().format('YYYY/MM')}`} variant="outlined" />
            <Chip label={`筆數：${rows.length}`} variant="outlined" />
          </Stack>
        </Stack>

        {/* ✅ 關鍵：右欄用 minmax(0,1fr) 避免溢出 */}
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', lg: '420px minmax(0, 1fr)' },
            alignItems: 'start',
          }}
        >
          {/* Left: Create */}
          <Paper sx={{ p: 2.2, borderRadius: 3, boxShadow: 2, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>新增產量</Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <Box sx={{ gridColumn: 'span 2' }}>
                <DatePicker
                  label="生產日期"
                  value={productionDate}
                  onChange={(v) => setProductionDate(v)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Box>

              <TextField size="small" label="機台" value={machine} onChange={(e) => setMachine(e.target.value)} />
              <TextField size="small" label="作業人員" value={operator} onChange={(e) => setOperator(e.target.value)} />

              <TextField
                size="small"
                label="圖號"
                value={drawingNo}
                onChange={(e) => setDrawingNo(e.target.value)}
                sx={{ gridColumn: 'span 2' }}
              />

              <FormControl size="small">
                <InputLabel id="dept-label">部門</InputLabel>
                <Select labelId="dept-label" label="部門" value={dept} onChange={(e) => setDept(e.target.value)}>
                  <MenuItem value=""><em>不指定</em></MenuItem>
                  <MenuItem value="塑膠">塑膠</MenuItem>
                  <MenuItem value="鈑金">鈑金</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small">
                <InputLabel id="attr-label">屬性</InputLabel>
                <Select labelId="attr-label" label="屬性" value={attr} onChange={(e) => setAttr(e.target.value)}>
                  <MenuItem value=""><em>不指定</em></MenuItem>
                  <MenuItem value="成品">成品</MenuItem>
                  <MenuItem value="半成品">半成品</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                label="數量"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                inputProps={{ inputMode: 'numeric' }}
                sx={{ gridColumn: 'span 2' }}
              />
            </Box>

            <Stack direction="row" spacing={1.2} sx={{ mt: 2 }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} disabled={loading} fullWidth>
                新增
              </Button>
              <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleResetCreate} disabled={loading} fullWidth>
                重設
              </Button>
            </Stack>
          </Paper>

          {/* Right: Search + Grid */}
          <Paper sx={{ p: 2.2, borderRadius: 3, boxShadow: 2, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 1, gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mr: 'auto' }}>
                生產查詢
              </Typography>

              <Button size="small" variant="outlined" startIcon={<ClearAllIcon />} onClick={handleResetSearch} disabled={loading}>
                清空條件
              </Button>

              <Button size="small" variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} disabled={loading}>
                查詢
              </Button>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* ✅ 查詢條件：auto-fit + minWidth，永遠不會把按鈕/畫面撐爆 */}
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              {/* ✅ 拿掉 DemoContainer，避免溢出 */}
              <DatePicker
                label="開始日期"
                value={qStart}
                onChange={(v) => setQStart(v)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
              <DatePicker
                label="結束日期"
                value={qEnd}
                onChange={(v) => setQEnd(v)}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />

              <TextField size="small" label="圖號" value={qDrawingNo} onChange={(e) => setQDrawingNo(e.target.value)} sx={{ minWidth: 180 }} />
              <TextField size="small" label="作業人員" value={qOperator} onChange={(e) => setQOperator(e.target.value)} sx={{ minWidth: 180 }} />

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="qDept-label">部門</InputLabel>
                <Select labelId="qDept-label" label="部門" value={qDept} onChange={(e) => setQDept(e.target.value)}>
                  <MenuItem value="全部">全部</MenuItem>
                  <MenuItem value="塑膠">塑膠</MenuItem>
                  <MenuItem value="鈑金">鈑金</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="qAttr-label">屬性</InputLabel>
                <Select labelId="qAttr-label" label="屬性" value={qAttr} onChange={(e) => setQAttr(e.target.value)}>
                  <MenuItem value="全部">全部</MenuItem>
                  <MenuItem value="成品">成品</MenuItem>
                  <MenuItem value="半成品">半成品</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{
              height: 560,
              width: '100%',
              minWidth: 0,
              '& .MuiDataGrid-root': { border: 'none', minWidth: 0 },
              '& .MuiDataGrid-columnHeaders': { bgcolor: 'rgba(0,0,0,0.03)' },
              '& .MuiDataGrid-row:nth-of-type(even)': { bgcolor: 'rgba(0,0,0,0.015)' },
              '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
            }}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                disableRowSelectionOnClick
                editMode="row"
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={(err) => {
                  console.error(err);
                  showToast('error', '更新時發生錯誤');
                }}
                pageSizeOptions={[10, 20, 50]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 20, page: 0 } },
                }}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 300 },
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>

        {/* 刪除確認 Dialog */}
        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>確認刪除</DialogTitle>
          <DialogContent>
            <DialogContentText>
              確定要刪除這筆資料嗎？
              <br />
              {deleteTarget
                ? `ID: ${deleteTarget.id} / 圖號: ${deleteTarget.drawingNo || ''} / 數量: ${deleteTarget.qty || ''}`
                : ''}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)} disabled={loading}>取消</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading} startIcon={<DeleteIcon />}>
              刪除
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={toast.open}
          autoHideDuration={2500}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={toast.severity} variant="filled">
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}
